<script setup lang="ts">
import {
	Ref,
	onMounted,
	ref,
} from 'vue';
import {
	EcommerceProductType,
	GOOGLE_TAG_MANAGER_EVENT,
} from '@zyro-inc/site-modules/types';
import { LAST_PURCHASE_COOKIE } from '@zyro-inc/site-modules/constants/siteModulesConstants';
import ModalContent from '@zyro-inc/site-modules/components/ecommerce/modals/partials/ModalContent.vue';
import ModalLayout from '@zyro-inc/site-modules/components/ecommerce/modals/partials/ModalLayout.vue';
import { useEcommerceGlobal } from '@zyro-inc/site-modules/use/useEcommerceGlobal';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';
import EventLogApi from '@zyro-inc/site-modules/api/EventLogApi';

import {
	getCookie,
	deleteCookie,
} from '@zyro-inc/site-modules/utils/cookies';
import { analyticsEvent } from '@zyro-inc/site-modules/utils/analyticsEvent';
import { getDomainWithoutWWWPrefix } from '@zyro-inc/site-modules/utils/domainUtils';

interface Props {
	translations: Record<string, string>,
}

const props = withDefaults(defineProps<Props>(), {
	translations: () => ({}),
});

const {
	isEcommerceStoreCreated,
	setShoppingCartItems,
} = useEcommerceGlobal();

const { closeEcommerceModal } = useEcommerceModal();

const modalContent = ref({
	title: props.translations.checkoutModalThanksForOrder,
	text: props.translations.checkoutModalOrderReceived,
	buttonText: props.translations.gotIt,
	modalType: 'success-order',
});
const modalRef = ref(null) as Ref<any>;

onMounted(() => {
	const params = new URLSearchParams(window.location.search);
	const productType = params.get('product');
	const leaveProducts = params.get('leave-products') === 'true';

	if (productType !== EcommerceProductType.BOOKING && !leaveProducts) {
		setShoppingCartItems([]);
	}

	if (productType === EcommerceProductType.BOOKING) {
		modalContent.value = {
			...modalContent.value,
			text: props.translations.checkoutModalSuccessfullyBooked,
			modalType: 'success-orderbooking',
		};
	} else if (productType === EcommerceProductType.DONATION) {
		modalContent.value = {
			...modalContent.value,
			title: props.translations.checkoutModalThanksForDonation,
			text: props.translations.checkoutModalDonationReceived,
			modalType: 'success-orderdonation',
		};
	} else if (productType === EcommerceProductType.DIGITAL) {
		modalContent.value = {
			...modalContent.value,
			text: props.translations.checkoutModalDigitalProductEmail,
			modalType: 'success-orderdigital',
		};
	}
});

onMounted(() => {
	const domainNameWithoutWww = getDomainWithoutWWWPrefix(window.location.hostname);
	const areCookiesAllowed = getCookie(`z-cookies-consent-${domainNameWithoutWww}`) !== '0';

	if (!areCookiesAllowed) {
		return;
	}

	const lastPurchaseCookie = getCookie(LAST_PURCHASE_COOKIE);

	if (lastPurchaseCookie) {
		try {
			const purchase = JSON.parse(lastPurchaseCookie);

			EventLogApi.logEvent({
				eventName: 'builder_customers.ecomm.order_successful',
				eventProperties: {
					payment_provider: purchase.payment_provider,
					value: purchase.value,
					currency: purchase.currency,
					transaction_id: purchase.transaction_id,
					shipping: purchase.shipping,
					tax: purchase.tax,
					items: purchase.items,
				},
			});

			if (purchase.is_new_checkout === true) {
				EventLogApi.logEvent({
					eventName: 'builder_customers.ecomm_new_checkout.order_successful',
					eventProperties: {
						payment_provider: purchase.payment_provider,
						device_mode: purchase.device_mode,
						is_express_checkout: false,
					},
				});
			}

			analyticsEvent(GOOGLE_TAG_MANAGER_EVENT.PURCHASE, {
				value: purchase.value,
				currency: purchase.currency,
				transaction_id: purchase.transaction_id,
				shipping: purchase.shipping,
				tax: purchase.tax,
				items: purchase.items,
			});
		} catch {
			// eslint-disable-next-line no-empty -- Do nothing, analytics related
		}

		deleteCookie(LAST_PURCHASE_COOKIE);
	} else {
		EventLogApi.logEvent({
			eventName: 'builder_customers.ecomm.order_successful',
		});
	}
});
</script>

<template>
	<ModalLayout
		v-if="isEcommerceStoreCreated"
		ref="modalRef"
		:pop-state-on-close="false"
	>
		<ModalContent
			:title="modalContent.title"
			:text="modalContent.text"
			:button-text="modalContent.buttonText"
			:qa-selector="modalContent.modalType"
			@handle-close="closeEcommerceModal"
		/>
	</ModalLayout>
</template>
